import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { path: '/services', label: 'Services' },
  { path: '/portfolio', label: 'Portfolio' },
  { path: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 bg-white ${
        scrolled ? 'shadow-[0_1px_0_rgba(0,0,0,0.06)]' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">

        {/* Brand */}
        <Link
          to="/"
          className="font-playfair text-base md:text-lg text-charcoal tracking-wide hover:text-bronze transition-colors duration-300"
          style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
        >
          Dhrumil Desai Photography
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10" aria-label="Main navigation">
          {navLinks.map(({ path, label }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `text-[11px] tracking-[0.22em] uppercase font-inter transition-colors duration-200 ${
                  isActive ? 'text-bronze' : 'text-charcoal hover:text-bronze'
                }`
              }
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              {label}
            </NavLink>
          ))}
          <div className="w-px h-4 bg-gray-200" aria-hidden="true" />
          <Link
            to="/contact"
            className="text-[11px] tracking-[0.22em] uppercase font-inter text-bronze border-b border-bronze pb-0.5 hover:text-charcoal hover:border-charcoal transition-colors duration-200"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Book a Session
          </Link>
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col justify-center items-center w-8 h-8 gap-1.5"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <span
            className={`block w-5 h-px bg-charcoal transition-all duration-300 origin-center ${
              menuOpen ? 'rotate-45 translate-y-[5px]' : ''
            }`}
          />
          <span
            className={`block w-5 h-px bg-charcoal transition-all duration-300 ${
              menuOpen ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`block w-5 h-px bg-charcoal transition-all duration-300 origin-center ${
              menuOpen ? '-rotate-45 -translate-y-[5px]' : ''
            }`}
          />
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <nav className="px-6 py-8 flex flex-col gap-6" aria-label="Mobile navigation">
              {navLinks.map(({ path, label }) => (
                <NavLink
                  key={path}
                  to={path}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-xs tracking-[0.22em] uppercase font-inter ${
                      isActive ? 'text-bronze' : 'text-charcoal'
                    }`
                  }
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  {label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className="text-xs tracking-[0.22em] uppercase font-inter text-bronze border-b border-bronze pb-0.5 self-start"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                Book a Session
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
